import { Controller, useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslations } from "use-intl";
import { FormInput } from "@/components/form/rhf/FormInput";
import { FormField } from "@/components/form/FormField";
import KnowledgePicker from "@/components/shared/pickers/knowledge-picker";
import MediaAssetPicker from "@/components/shared/pickers/media-asset-picker";
import ToolsAllowedPicker from "@/components/shared/pickers/tools-allowed-picker";
import { useFeatureEnabled } from "@/features/features";
import { useUpdateStep } from "@/hooks/api/mutations/workflows.mutations";
import type { WorkflowStep } from "@/types/workflow.types";
import { AgenticModelSelect } from "./agentic-model-select";
import { HighlightedCodeEditor } from "./highlighted-code-editor";

const schema = z.object({
  modelId: z.string().min(1, "Model is required"),
  systemPrompt: z.string().min(1, "System prompt is required"),
  maxIterations: z.number().int().min(1).max(50),
  temperature: z.number().min(0).max(2).optional(),
  knowledgeIds: z.array(z.string()),
  mediaAssetIds: z.array(z.string()),
  toolsAllowed: z.array(z.string()),
});

type FormValues = z.infer<typeof schema>;

function toFormValues(config: Record<string, unknown>): FormValues {
  return {
    modelId: String(config.model_id ?? ""),
    systemPrompt: String(config.system_prompt ?? ""),
    maxIterations: typeof config.max_iterations === "number" ? config.max_iterations : 10,
    temperature: typeof config.temperature === "number" ? config.temperature : undefined,
    knowledgeIds: Array.isArray(config.knowledge_ids) ? (config.knowledge_ids as string[]) : [],
    mediaAssetIds: Array.isArray(config.media_asset_ids)
      ? (config.media_asset_ids as string[])
      : [],
    toolsAllowed: Array.isArray(config.tools) ? (config.tools as string[]) : [],
  };
}

function toPayload(values: FormValues): Record<string, unknown> {
  return {
    model_id: values.modelId,
    system_prompt: values.systemPrompt,
    max_iterations: values.maxIterations,
    temperature: values.temperature ?? null,
    knowledge_ids: values.knowledgeIds,
    media_asset_ids: values.mediaAssetIds,
    tools: values.toolsAllowed,
  };
}

interface AgentTaskConfigFormProps {
  step: WorkflowStep;
  workflowId: string;
  onSuccess?: () => void;
}

export function AgentTaskConfigForm({ step, workflowId, onSuccess }: AgentTaskConfigFormProps) {
  const t = useTranslations("workflows");
  const updateStep = useUpdateStep();
  const kbEnabled = useFeatureEnabled("knowledge_base");

  const methods = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: toFormValues(step.config),
  });

  const {
    handleSubmit,
    watch,
    setValue,
    control,
    formState: { errors, isSubmitting },
  } = methods;

  const knowledgeIds = watch("knowledgeIds");
  const mediaAssetIds = watch("mediaAssetIds");
  const toolsAllowed = watch("toolsAllowed");
  const maxIterations = watch("maxIterations");

  const onSubmit = handleSubmit(async (values) => {
    await updateStep.mutateAsync({ workflowId, stepId: step.id, config: toPayload(values) });
    onSuccess?.();
  });

  return (
    <FormProvider {...methods}>
      <form onSubmit={onSubmit} className="flex flex-col gap-5">
        <AgenticModelSelect
          name="modelId"
          label={t("drawer.field.model_id")}
          required
        />

        <FormField
          label={t("drawer.field.system_prompt")}
          required
          error={errors.systemPrompt?.message}
        >
          <Controller
            control={control}
            name="systemPrompt"
            render={({ field }) => (
              <HighlightedCodeEditor
                language="markdown"
                value={field.value}
                onChange={(event) => field.onChange(event.target.value)}
                onBlur={field.onBlur}
                rows={6}
                minHeight={150}
                placeholder="You are a helpful assistant…"
                invalid={!!errors.systemPrompt}
              />
            )}
          />
        </FormField>

        <FormField
          label={`${t("drawer.field.max_iterations")} (${maxIterations})`}
          error={errors.maxIterations?.message}
        >
          <input
            type="range"
            min={1}
            max={50}
            {...methods.register("maxIterations", { valueAsNumber: true })}
            className="w-full accent-[var(--primary)]"
          />
          <div className="flex justify-between text-[10px] text-[var(--muted-foreground)]">
            <span>1</span>
            <span>50</span>
          </div>
        </FormField>

        <FormInput
          name="temperature"
          label={t("drawer.field.temperature")}
          type="decimal"
          placeholder="0.7"
        />

        <FormField label={t("drawer.field.tools_allowed")}>
          <ToolsAllowedPicker
            value={toolsAllowed}
            onChange={(ids) => setValue("toolsAllowed", ids, { shouldDirty: true })}
            nodeType={step.stepType}
            fieldKey="tools"
          />
        </FormField>

        <FormField label={t("drawer.field.knowledge_ids")}>
          <KnowledgePicker
            value={knowledgeIds}
            onChange={(ids) => setValue("knowledgeIds", ids, { shouldDirty: true })}
            disabled={!kbEnabled}
            placeholder={kbEnabled ? undefined : t("drawer.feature_disabled.knowledge_base")}
            nodeType={step.stepType}
            fieldKey="knowledge_ids"
          />
        </FormField>

        <FormField label={t("drawer.field.media_asset_ids")}>
          <MediaAssetPicker
            value={mediaAssetIds}
            onChange={(ids) => setValue("mediaAssetIds", ids, { shouldDirty: true })}
            nodeType={step.stepType}
            fieldKey="media_asset_ids"
          />
        </FormField>

        {updateStep.isError && (
          <p className="text-xs text-[var(--destructive)]">{t("drawer.save_error")}</p>
        )}

        <button
          type="submit"
          disabled={isSubmitting || updateStep.isPending}
          className="h-9 w-full rounded-lg bg-[var(--primary)] text-sm font-medium text-[var(--primary-foreground)] hover:opacity-90 disabled:opacity-60"
        >
          {isSubmitting || updateStep.isPending ? t("drawer.saving") : t("drawer.save")}
        </button>
      </form>
    </FormProvider>
  );
}

export default AgentTaskConfigForm;
